import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import BottomSheetModal from "./BottomSheetModal";
import { StepHeader, SuccessIcon } from "./SheetPrimitives";
import CustomInput from "../AuthenticationComponents/CustomInput";
import CustomButton from "../AuthenticationComponents/CustomButton";

import giftIcon from "../../assets/images/wallet/gift-white.svg";
import arrowRightIcon from "../../assets/images/authentication/arrow-right.svg";

// API
import { post } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage } from "../../utils/errorHandler";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// QR Scanner
import { Html5Qrcode } from "html5-qrcode";

/* -------------------------------------------------------------------------
 * RestaurantRedeemVoucherModal — Internal voucher redemption modal for
 * restaurant staff. Allows manual code entry or QR scanning to redeem
 * customer vouchers. Built on the shared BottomSheetModal shell.
 * ---------------------------------------------------------------------- */

const RestaurantRedeemVoucherModal = ({ isOpen, onClose, onVoucherRedeemed }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [code, setCode] = useState("");
  const [success, setSuccess] = useState(false);
  const [redeemedVoucher, setRedeemedVoucher] = useState(null);
  const scannerRef = useRef(null);
  const errorToastShownRef = useRef(false);
  const isScanningRef = useRef(false);

  // Configure toastr options
  useEffect(() => {
    toastr.options = {
      positionClass: "toast-bottom-left",
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
    };
  }, []);

  // Check if device has camera access (for QR scanning)
  useEffect(() => {
    const checkCameraAccess = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some(
          (device) => device.kind === "videoinput",
        );
        setIsMobileOrTablet(hasCamera);
      } catch (error) {
        // Fallback to user agent detection if camera check fails
        const userAgent =
          navigator.userAgent || navigator.vendor || window.opera;
        const isMobile =
          /android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
        const isTablet = /ipad|android(?!.*mobile)|tablet/i.test(userAgent);
        setIsMobileOrTablet(isMobile || isTablet);
      }
    };

    checkCameraAccess();

    // Cleanup refs on component unmount
    return () => {
      errorToastShownRef.current = false;
      isScanningRef.current = false;
    };
  }, []);

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setCode("");
      setError("");
      setSuccess(false);
      setRedeemedVoucher(null);
      setShowScanner(false);
      setCameraError("");
      errorToastShownRef.current = false;
    }
  }, [isOpen]);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (err) {
        console.error("Scanner stop error:", err);
      } finally {
        scannerRef.current = null;
        isScanningRef.current = false;
      }
    }
  };

  const handleScanQR = async () => {
    // Check if running on HTTPS (required for camera access)
    if (
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"
    ) {
      toastr.error(
        "Camera access requires HTTPS. Please use a secure connection.",
      );
      return;
    }

    if (!isMobileOrTablet) {
      toastr.warning(
        "QR scanning is only available on mobile and tablet devices",
      );
      return;
    }

    // Reset error state and open scanner
    setCameraError("");
    errorToastShownRef.current = false;
    setShowScanner(true);
  };

  const handleScanResult = (decodedText) => {
    if (decodedText) {
      // Clear any error state on successful scan
      setCameraError("");
      errorToastShownRef.current = false;

      // Extract code from URL if present
      let codeValue = decodedText;
      if (decodedText.includes("code=")) {
        try {
          const url = new URL(decodedText);
          const codeParam = url.searchParams.get("code");
          if (codeParam) {
            codeValue = codeParam;
          }
        } catch (err) {
          // Not a valid URL, use decodedText as-is
        }
      }

      setCode(codeValue);
      setShowScanner(false);
      stopScanner();
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;

    // Check if the pasted content is a URL with a code parameter
    if (value.includes("code=")) {
      try {
        const url = new URL(value);
        const codeParam = url.searchParams.get("code");
        if (codeParam) {
          setCode(codeParam);
          return;
        }
      } catch (err) {
        // Not a valid URL, proceed with normal handling
      }
    }

    // Normal handling
    setCode(value);
  };

  // NOTE (fix): this callback is html5-qrcode's per-frame
  // "qrCodeErrorCallback". It fires many times per second for every
  // single frame in which no QR code happens to be decodable — which is
  // completely normal while the camera is actively scanning and simply
  // hasn't found a code yet. It is NOT a camera failure signal.
  const handleScanError = () => {
    // Intentionally a no-op: per-frame "no code found yet" events are
    // expected and should not be treated as errors.
  };

  const handleCloseScanner = async () => {
    setShowScanner(false);
    setCameraError("");
    errorToastShownRef.current = false;
    await stopScanner();
  };

  // Initialize and start scanner when modal opens
  useEffect(() => {
    if (!showScanner) {
      return undefined;
    }

    // Reset error state when opening scanner
    setCameraError("");
    errorToastShownRef.current = false;

    const html5QrCode = new Html5Qrcode("qr-reader");
    scannerRef.current = html5QrCode;

    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0,
    };

    html5QrCode
      .start(
        { facingMode: "environment" },
        config,
        handleScanResult,
        handleScanError,
      )
      .then(() => {
        // Scanner started successfully, clear any previous errors
        isScanningRef.current = true;
        setCameraError("");
      })
      .catch((err) => {
        console.error("Scanner start error:", err);
        isScanningRef.current = false;
        // Don't show toastr on scanner start failure - only set error state
        setCameraError(
          "Unable to start camera. Please check camera permissions.",
        );
      });

    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showScanner]);

  const handleRedeem = async () => {
    if (!code.trim()) {
      setError("Please enter the voucher code");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await post(endpoints.restaurantVouchersRedeem, {
        code: code.trim(),
      });

      const isSuccess = response.data?.success || response.data?.status;

      if (isSuccess) {
        const voucherData = response.data?.data?.voucher || response.data?.data;
        setRedeemedVoucher({
          amount: voucherData?.amount_cents ? voucherData.amount_cents / 100 : voucherData?.amount || 0,
          customerName: voucherData?.customer?.name || "Customer",
        });
        setSuccess(true);
        toastr.success(
          response.data?.message ||
            response.message ||
            "Voucher redeemed successfully!",
        );
        setCode("");
        
        if (onVoucherRedeemed) {
          onVoucherRedeemed();
        }
      } else {
        setError(
          response.data?.message ||
            response.message ||
            "Voucher redemption failed",
        );
        toastr.error(
          response.data?.message ||
            response.message ||
            "Voucher redemption failed",
        );
      }
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage);
      toastr.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Render QR scanner overlay
  const renderScanner = () => {
    if (!showScanner) return null;

    return (
      <div className="qr-scanner-overlay">
        <div className="qr-scanner-modal">
          <div className="qr-scanner-header">
            <h3>Scan QR Code</h3>
            <button
              type="button"
              onClick={handleCloseScanner}
              className="qr-scanner-close"
            >
              ×
            </button>
          </div>
          <div className="qr-scanner-container">
            <div
              id="qr-reader"
              style={{ width: "100%", height: "100%" }}
            ></div>
            {cameraError && (
              <div
                className="qr-scanner-error"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  background: "rgba(0,0,0,0.8)",
                  color: "white",
                  padding: "16px",
                  borderRadius: "8px",
                  textAlign: "center",
                  maxWidth: "80%",
                }}
              >
                {cameraError}
              </div>
            )}
          </div>
          <p className="qr-scanner-hint">
            Point your camera at the QR code
          </p>
        </div>
      </div>
    );
  };

  // Render success state
  const renderSuccess = () => {
    return (
      <div className="rrvm-content-wrapper">
        <div className="vam-wrap">
          <p className="mc-eyebrow">Redemption Successful</p>
          <SuccessIcon icon={giftIcon} tone="green" />
          <h2 className="mc-title mc-title--center" id="rrvm-title">
            Voucher Redeemed!
          </h2>
          <p className="vam-highlight">
            ${redeemedVoucher?.amount || 0} voucher from {redeemedVoucher?.customerName || "Customer"}
          </p>
          <p className="mc-description mc-description--center">
            The voucher has been successfully redeemed.
          </p>
          <CustomButton
            type="button"
            variant="primary"
            iconPosition="right"
            icon={arrowRightIcon}
            className="w-100"
            onClick={handleClose}
          >
            Done
          </CustomButton>
        </div>
      </div>
    );
  };

  // Render form state
  const renderForm = () => {
    return (
      <div className="rrvm-content-wrapper">
        <StepHeader
          title="Redeem Voucher"
          subtitle="Enter the customer's 12-character voucher code"
          titleId="rrvm-title"
        />

        <div className="custom-input-row">
          <CustomInput
            label="Voucher Code"
            name="code"
            type="text"
            placeholder="XXXX-XXXX-XXXX"
            value={code}
            onChange={handleCodeChange}
            className="white-bg"
          />
        </div>

        {error ? <p className="auth-server-error">{error}</p> : null}

        <div className="rrvm-button-group">
          {isMobileOrTablet && (
            <CustomButton
              type="button"
              variant="secondary"
              onClick={handleScanQR}
              className="w-100"
              iconPosition="right"
              icon={arrowRightIcon}
            >
              Scan QR Code
            </CustomButton>
          )}

          <CustomButton
            type="button"
            variant="primary"
            iconPosition="right"
            icon={arrowRightIcon}
            loading={loading}
            onClick={handleRedeem}
            disabled={!code.trim()}
            className="w-100"
          >
            Redeem Voucher
          </CustomButton>
        </div>
      </div>
    );
  };

  return (
    <>
      <BottomSheetModal isOpen={isOpen} onClose={onClose} labelledBy="rrvm-title">
        {success ? renderSuccess() : renderForm()}
      </BottomSheetModal>
      {renderScanner()}
    </>
  );
};

RestaurantRedeemVoucherModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onVoucherRedeemed: PropTypes.func,
};

RestaurantRedeemVoucherModal.defaultProps = {
  onVoucherRedeemed: () => {},
};

export default RestaurantRedeemVoucherModal;
